import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  Users,
  Save,
  Search,
  Filter,
  CheckSquare,
  Square,
  CheckCircle,
  UserPlus,
  X,
  AlertCircle,
  AlertTriangle,
} from "lucide-react";

export default function KelolaRelasi() {
  const [tutorList, setTutorList] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [relasiList, setRelasiList] = useState([]);
  const [loading, setLoading] = useState(true);

  // STATE NOTIFIKASI MODERN
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    idToDelete: null,
  });

  // STATE FORM MASSAL
  const [selectedTutor, setSelectedTutor] = useState("");
  const [selectedSiswaIDs, setSelectedSiswaIDs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FILTER STATE
  const [filterSearch, setFilterSearch] = useState("");
  const [filterKelas, setFilterKelas] = useState("");
  const [searchHistory, setSearchHistory] = useState("");

  const opsiKelas = [
    "TK",
    "1 SD",
    "2 SD",
    "3 SD",
    "4 SD",
    "5 SD",
    "6 SD",
    "7 SMP",
    "8 SMP",
    "9 SMP",
    "10 SMA",
    "11 SMA",
    "12 SMA",
    "Umum",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // --- HELPER NOTIFIKASI ---
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 3000);
  };

  async function fetchData() {
    setLoading(true);
    const { data: dtTutor } = await supabase
      .from("tutor")
      .select("*")
      .order("nama_lengkap");
    const { data: dtSiswa } = await supabase
      .from("siswa")
      .select("*")
      .order("nama_lengkap");
    const { data: dtRelasi } = await supabase
      .from("relasi_siswa_tutor")
      .select("*, tutor(nama_lengkap, mapel), siswa(nama_lengkap, kelas)")
      .order("id", { ascending: false });

    setTutorList(dtTutor || []);
    setSiswaList(dtSiswa || []);
    setRelasiList(dtRelasi || []);
    setLoading(false);
  }

  // --- LOGIKA UTAMA ---
  const getFilteredSiswa = () => {
    return siswaList.filter((s) => {
      const matchName = s.nama_lengkap
        .toLowerCase()
        .includes(filterSearch.toLowerCase());
      const matchKelas = filterKelas ? s.kelas === filterKelas : true;
      return matchName && matchKelas;
    });
  };

  const filteredSiswa = getFilteredSiswa();

  const isAlreadyAssignedToCurrentTutor = (siswaId) => {
    if (!selectedTutor) return false;
    return relasiList.some(
      (r) => r.tutor_id == selectedTutor && r.siswa_id == siswaId
    );
  };

  const toggleSiswa = (id) => {
    if (isAlreadyAssignedToCurrentTutor(id)) return;
    if (selectedSiswaIDs.includes(id)) {
      setSelectedSiswaIDs((prev) => prev.filter((itemId) => itemId !== id));
    } else {
      setSelectedSiswaIDs((prev) => [...prev, id]);
    }
  };

  const handleSelectAllVisible = () => {
    const availableIDs = filteredSiswa
      .filter((s) => !isAlreadyAssignedToCurrentTutor(s.id))
      .map((s) => s.id);

    const isAllSelected =
      availableIDs.length > 0 &&
      availableIDs.every((id) => selectedSiswaIDs.includes(id));

    if (isAllSelected) {
      setSelectedSiswaIDs((prev) =>
        prev.filter((id) => !availableIDs.includes(id))
      );
    } else {
      const newSet = new Set([...selectedSiswaIDs, ...availableIDs]);
      setSelectedSiswaIDs(Array.from(newSet));
    }
  };

  // --- HANDLER SUBMIT & DELETE ---
  async function handleSubmit() {
    if (!selectedTutor)
      return showToast("Silakan pilih Tutor terlebih dahulu!", "error");
    if (selectedSiswaIDs.length === 0)
      return showToast("Pilih minimal 1 siswa dari daftar!", "error");

    setIsSubmitting(true);
    try {
      const payload = selectedSiswaIDs.map((idSiswa) => ({
        tutor_id: selectedTutor,
        siswa_id: idSiswa,
      }));

      const { error } = await supabase
        .from("relasi_siswa_tutor")
        .insert(payload);
      if (error) throw error;

      showToast(
        `Berhasil menambahkan ${selectedSiswaIDs.length} siswa ke kelas!`,
        "success"
      );
      setSelectedSiswaIDs([]);
      fetchData();
    } catch (error) {
      showToast("Gagal menyimpan: " + error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  // Buka Modal Konfirmasi
  const handleDeleteClick = (id) => {
    setConfirmModal({ show: true, idToDelete: id });
  };

  // Eksekusi Hapus
  const confirmDelete = async () => {
    try {
      await supabase
        .from("relasi_siswa_tutor")
        .delete()
        .eq("id", confirmModal.idToDelete);
      showToast("Siswa berhasil dikeluarkan dari kelas.", "success");
      fetchData();
    } catch (error) {
      showToast("Gagal menghapus.", "error");
    } finally {
      setConfirmModal({ show: false, idToDelete: null });
    }
  };

  const getFilteredHistory = () => {
    if (!searchHistory) return relasiList;
    return relasiList.filter(
      (item) =>
        item.tutor?.nama_lengkap
          .toLowerCase()
          .includes(searchHistory.toLowerCase()) ||
        item.siswa?.nama_lengkap
          .toLowerCase()
          .includes(searchHistory.toLowerCase())
    );
  };

  // --- STYLES ---
  const styles = {
    container: {
      padding: "30px",
      maxWidth: "1200px",
      margin: "0 auto",
      fontFamily: "'Inter', sans-serif",
      background: "#F8FAFC",
      minHeight: "100vh",
      position: "relative",
    },

    // TOAST
    toastContainer: {
      position: "fixed",
      top: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 20px",
      borderRadius: "12px",
      background: "white",
      boxShadow: "0 10px 20px rgba(0,0,0,0.15)",
      border: "1px solid #E2E8F0",
      minWidth: "320px",
      animation: "slideDown 0.3s ease-out",
    },

    // MODAL
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(15, 23, 42, 0.6)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(4px)",
    },
    modalContent: {
      background: "white",
      padding: "30px",
      borderRadius: "20px",
      maxWidth: "400px",
      width: "90%",
      textAlign: "center",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },

    // UI UTAMA
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "350px 1fr",
      gap: "24px",
      alignItems: "start",
    },
    card: {
      background: "white",
      borderRadius: "16px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
      overflow: "hidden",
    },
    select: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #CBD5E1",
      marginBottom: "15px",
      background: "white",
      fontSize: "14px",
      outline: "none",
    },
    btnPrimary: {
      width: "100%",
      background: "#2563EB",
      color: "white",
      padding: "14px",
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontWeight: "bold",
      fontSize: "15px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "0.2s",
    },

    // TABEL
    table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
    th: {
      textAlign: "left",
      padding: "12px 20px",
      background: "#F8FAFC",
      color: "#64748B",
      fontSize: "12px",
      textTransform: "uppercase",
      borderBottom: "1px solid #E2E8F0",
    },
    td: {
      padding: "12px 20px",
      borderBottom: "1px solid #F1F5F9",
      color: "#334155",
    },
  };

  return (
    <div style={styles.container}>
      {/* 1. TOAST NOTIFICATION COMPONENT */}
      {toast.show && (
        <div
          style={{
            ...styles.toastContainer,
            borderLeft:
              toast.type === "success"
                ? "4px solid #10B981"
                : "4px solid #EF4444",
          }}
        >
          {toast.type === "success" ? (
            <div
              style={{ background: "#D1FAE5", padding: 6, borderRadius: "50%" }}
            >
              <CheckCircle size={20} color="#059669" />
            </div>
          ) : (
            <div
              style={{ background: "#FEE2E2", padding: 6, borderRadius: "50%" }}
            >
              <AlertCircle size={20} color="#DC2626" />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div
              style={{ fontWeight: "bold", fontSize: "14px", color: "#1E293B" }}
            >
              {toast.type === "success" ? "Berhasil!" : "Perhatian"}
            </div>
            <div style={{ fontSize: "13px", color: "#64748B" }}>
              {toast.message}
            </div>
          </div>
          <button
            onClick={() => setToast({ ...toast, show: false })}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <X size={16} color="#94A3B8" />
          </button>
        </div>
      )}

      {/* 2. CONFIRM DELETE MODAL COMPONENT */}
      {confirmModal.show && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div
              style={{
                width: 60,
                height: 60,
                background: "#FEE2E2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px auto",
              }}
            >
              <AlertTriangle size={32} color="#DC2626" />
            </div>
            <h3
              style={{
                margin: "0 0 10px 0",
                color: "#1E293B",
                fontSize: "20px",
              }}
            >
              Hapus Siswa dari Kelas?
            </h3>
            <p
              style={{
                margin: "0 0 25px 0",
                color: "#64748B",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Siswa ini tidak akan terhubung lagi dengan tutor tersebut. Data
              nilai yang sudah ada tidak akan hilang.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() =>
                  setConfirmModal({ show: false, idToDelete: null })
                }
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #CBD5E1",
                  background: "white",
                  color: "#64748B",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Batal
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#DC2626",
                  color: "white",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ marginBottom: "30px" }}>
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            textDecoration: "none",
            color: "#64748B",
            marginBottom: "15px",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <ArrowLeft size={18} /> Kembali ke Dashboard
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
          <div style={{ background: "#DBEAFE", padding: 12, borderRadius: 16 }}>
            <Users size={32} color="#2563EB" />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                color: "#1E293B",
                fontSize: "24px",
                fontWeight: "800",
              }}
            >
              Atur Kelas Massal
            </h1>
            <p
              style={{
                margin: "5px 0 0 0",
                color: "#64748B",
                fontSize: "14px",
              }}
            >
              Manajemen pembagian kelas siswa dan tutor
            </p>
          </div>
        </div>
      </div>

      <div style={styles.gridContainer}>
        {/* PANEL KIRI */}
        <div
          style={styles.card}
          style={{ position: "sticky", top: 20, ...styles.card }}
        >
          <div
            style={{
              padding: "20px",
              borderBottom: "1px solid #F1F5F9",
              fontSize: "16px",
              fontWeight: "700",
              color: "#1E293B",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <CheckCircle size={20} color="#2563EB" /> 1. Pilih Tutor
          </div>
          <div style={{ padding: "20px" }}>
            <label style={styles.label}>Tutor Pengajar</label>
            <select
              style={{
                ...styles.select,
                borderColor: selectedTutor ? "#2563EB" : "#CBD5E1",
              }}
              value={selectedTutor}
              onChange={(e) => {
                setSelectedTutor(e.target.value);
                setSelectedSiswaIDs([]);
              }}
            >
              <option value="">-- Pilih Tutor --</option>
              {tutorList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nama_lengkap} ({t.mapel})
                </option>
              ))}
            </select>

            <div
              style={{
                background: "#F0F9FF",
                border: "1px dashed #BAE6FD",
                borderRadius: "12px",
                padding: "15px",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#64748B",
                  textTransform: "uppercase",
                }}
              >
                Siswa Terpilih
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: "800",
                  color: "#0284C7",
                  margin: "5px 0",
                }}
              >
                {selectedSiswaIDs.length}
              </div>
              <div style={{ fontSize: 12, color: "#0369A1" }}>
                Siap ditambahkan
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={
                !selectedTutor || selectedSiswaIDs.length === 0 || isSubmitting
              }
              style={{
                ...styles.btnPrimary,
                opacity:
                  !selectedTutor || selectedSiswaIDs.length === 0 ? 0.6 : 1,
              }}
            >
              {isSubmitting ? (
                "Menyimpan..."
              ) : (
                <>
                  <Save size={18} /> SIMPAN KELAS
                </>
              )}
            </button>
          </div>
        </div>

        {/* PANEL KANAN */}
        <div style={styles.card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "15px 20px",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontWeight: 700,
                color: "#334155",
              }}
            >
              <UserPlus size={20} color="#2563EB" /> 2. Pilih Siswa
            </div>
            {selectedTutor && (
              <button
                onClick={handleSelectAllVisible}
                style={{
                  background: "#F1F5F9",
                  border: "1px solid #E2E8F0",
                  padding: "6px 12px",
                  borderRadius: 6,
                  fontSize: 12,
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#475569",
                }}
              >
                Pilih Semua
              </button>
            )}
          </div>

          <div
            style={{
              padding: "10px 20px",
              background: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
              display: "flex",
              gap: 10,
            }}
          >
            <div style={{ position: "relative", flex: 1 }}>
              <Search
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  color: "#94A3B8",
                }}
              />
              <input
                style={{
                  flex: 1,
                  padding: "8px 12px 8px 30px",
                  borderRadius: "6px",
                  border: "1px solid #CBD5E1",
                  fontSize: "13px",
                  outline: "none",
                  width: "100%",
                  boxSizing: "border-box",
                }}
                placeholder="Cari nama siswa..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />
            </div>
            <div style={{ position: "relative", width: "140px" }}>
              <Filter
                size={14}
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  color: "#94A3B8",
                }}
              />
              <select
                style={{
                  width: "100%",
                  padding: "8px 12px 8px 30px",
                  borderRadius: "6px",
                  border: "1px solid #CBD5E1",
                  fontSize: "13px",
                  outline: "none",
                  background: "white",
                }}
                value={filterKelas}
                onChange={(e) => setFilterKelas(e.target.value)}
              >
                <option value="">Semua Kelas</option>
                {opsiKelas.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              height: "500px",
              overflowY: "auto",
              background: "#FFFFFF",
            }}
          >
            {!selectedTutor ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94A3B8",
                  flexDirection: "column",
                }}
              >
                <Users size={40} style={{ marginBottom: 10, opacity: 0.3 }} />
                Pilih Tutor di panel kiri dulu.
              </div>
            ) : filteredSiswa.length === 0 ? (
              <div
                style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}
              >
                Siswa tidak ditemukan.
              </div>
            ) : (
              filteredSiswa.map((siswa) => {
                const assigned = isAlreadyAssignedToCurrentTutor(siswa.id);
                const isSelected = selectedSiswaIDs.includes(siswa.id);
                return (
                  <div
                    key={siswa.id}
                    onClick={() => toggleSiswa(siswa.id)}
                    style={{
                      padding: "12px 20px",
                      borderBottom: "1px solid #F1F5F9",
                      cursor: assigned ? "not-allowed" : "pointer",
                      background: assigned
                        ? "#F8FAFC"
                        : isSelected
                        ? "#EFF6FF"
                        : "white",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      transition: "0.1s",
                    }}
                  >
                    <div style={{ color: isSelected ? "#2563EB" : "#CBD5E1" }}>
                      {assigned ? (
                        <CheckSquare size={20} color="#94A3B8" />
                      ) : isSelected ? (
                        <CheckSquare size={20} />
                      ) : (
                        <Square size={20} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: assigned ? "#94A3B8" : "#334155",
                          fontSize: 14,
                        }}
                      >
                        {siswa.nama_lengkap}
                      </div>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        {siswa.kelas} {siswa.kelompok && `• ${siswa.kelompok}`}
                      </div>
                    </div>
                    {assigned && (
                      <span
                        style={{
                          fontSize: 11,
                          background: "#E2E8F0",
                          color: "#64748B",
                          padding: "2px 6px",
                          borderRadius: 4,
                        }}
                      >
                        Terdaftar
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 40 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 15,
          }}
        >
          <h3 style={{ margin: 0, color: "#334155" }}>
            Daftar Kelas Aktif ({relasiList.length})
          </h3>
          <input
            placeholder="Cari di riwayat..."
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              border: "1px solid #CBD5E1",
              fontSize: 13,
              outline: "none",
            }}
            value={searchHistory}
            onChange={(e) => setSearchHistory(e.target.value)}
          />
        </div>

        <div
          style={{
            background: "white",
            borderRadius: 12,
            border: "1px solid #E2E8F0",
            overflow: "hidden",
          }}
        >
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nama Tutor</th>
                <th style={styles.th}>Nama Siswa</th>
                <th style={styles.th}>Kelas</th>
                <th style={{ ...styles.th, textAlign: "center" }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredHistory().map((item, idx) => (
                <tr
                  key={item.id}
                  style={{ background: idx % 2 === 0 ? "white" : "#F8FAFC" }}
                >
                  <td style={styles.td}>
                    <div style={{ fontWeight: "600", color: "#2563EB" }}>
                      {item.tutor?.nama_lengkap}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748B" }}>
                      {item.tutor?.mapel}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: "600", color: "#334155" }}>
                      {item.siswa?.nama_lengkap}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        background: "#F1F5F9",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        color: "#475569",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      {item.siswa?.kelas}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <button
                      onClick={() => handleDeleteClick(item.id)}
                      style={{
                        background: "#FEF2F2",
                        border: "1px solid #FEE2E2",
                        color: "#EF4444",
                        padding: "6px",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                      title="Hapus"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {getFilteredHistory().length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      padding: 20,
                      textAlign: "center",
                      color: "#94A3B8",
                    }}
                  >
                    Data tidak ditemukan
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`@keyframes slideDown { from {transform: translate(-50%, -100%); opacity:0;} to {transform: translate(-50%, 0); opacity:1;} }`}</style>
    </div>
  );
}
